import { FINAL_WORD_MARKER_ORDER, MARKERS, type MarkerId } from '@/constants/MARKERS'
import { safeStorage } from '@/lib/safe-storage'

const STORAGE_KEY = 'mosquarium_collected_letters'
const FACTS_KEY = 'mosquarium_seen_facts'
export const FINAL_WORD = 'ЭКОЛОГИКА'
export type MarkerFactId = `${MarkerId}:${number}`

/**
 * Возвращает markerId меток, игры которых пользователь уже прошёл.
 * Храним именно markerId, а не буквы: в слове есть повторяющиеся "К" и "О",
 * поэтому буква сама по себе не может однозначно описать прогресс.
 */
export function getCollectedMarkers(): MarkerId[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = safeStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return parsed.filter((markerId): markerId is MarkerId => typeof markerId === 'string' && markerId in MARKERS)
  } catch {
    return []
  }
}

/**
 * Добавляет пройденную метку в localStorage. Операция идемпотентная:
 * повторное прохождение той же игры не создаёт дубль в прогрессе.
 */
export function addCollectedMarker(markerId: MarkerId): void {
  const current = getCollectedMarkers()
  if (current.includes(markerId)) return
  safeStorage.setItem(STORAGE_KEY, JSON.stringify([...current, markerId]))
}

/**
 * Возвращает сетку букв в порядке финального слова "ЭКОЛОГИКА".
 * Порядок задаётся через FINAL_WORD_MARKER_ORDER, чтобы одинаковые буквы
 * оставались привязаны к конкретным меткам и не путались между собой.
 */
export function getLettersGrid(): Array<{ letter: string; collected: boolean; markerId: MarkerId | null }> {
  const collected = new Set(getCollectedMarkers())
  return FINAL_WORD_MARKER_ORDER.map(markerId => ({
    markerId,
    letter: MARKERS[markerId].letter,
    collected: collected.has(markerId),
  }))
}

/**
 * true, когда пользователь прошёл все 9 меток маршрута и собрал всё слово.
 */
export function isAllLettersCollected(): boolean {
  return getCollectedMarkers().length >= FINAL_WORD_MARKER_ORDER.length
}

/**
 * Сброс прогресса для отладки полного прохождения квеста.
 * В продуктовый UI не выводим; сейчас вызывается через `/?reset=letters`.
 */
export function resetLettersProgress(): void {
  if (typeof window === 'undefined') return
  safeStorage.removeItem(STORAGE_KEY)
}

export function markerFactId(markerId: MarkerId, factIndex: number): MarkerFactId {
  return `${markerId}:${factIndex}`
}

function parseSeenFact(value: unknown): MarkerFactId | null {
  if (typeof value !== 'string') return null

  if (value in MARKERS) {
    return markerFactId(value as MarkerId, 0)
  }

  const [markerId, index] = value.split(':')
  if (!(markerId in MARKERS)) return null

  const factIndex = Number(index)
  if (!Number.isInteger(factIndex) || factIndex < 0 || factIndex > 1) return null

  return markerFactId(markerId as MarkerId, factIndex)
}

/**
 * Возвращает markerId:index фактов, которые пользователь уже видел. Этот список
 * нужен для аналитики/общего знания, но не управляет показом факта в игре:
 * в каждой новой игровой сессии факты снова открываются на своих порогах прогресса.
 */
export function getSeenFacts(): MarkerFactId[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = safeStorage.getItem(FACTS_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    if (!Array.isArray(parsed)) return []
    return Array.from(new Set(parsed.map(parseSeenFact).filter((id): id is MarkerFactId => Boolean(id))))
  } catch {
    return []
  }
}

/**
 * Помечает факт метки как просмотренный для localStorage-аналитики.
 * Не влияет на сессионный показ BottomSheet: факты должны появляться каждый
 * раз при новом прохождении игры, когда счётчик доходит до нужного порога.
 */
export function markFactSeen(markerId: MarkerId, factIndex: number): void {
  const current = getSeenFacts()
  const id = markerFactId(markerId, factIndex)
  if (current.includes(id)) return
  safeStorage.setItem(FACTS_KEY, JSON.stringify([...current, id]))
}

/**
 * Сброс просмотренных фактов для тестирования середины игры.
 */
export function resetFactsProgress(): void {
  if (typeof window === 'undefined') return
  safeStorage.removeItem(FACTS_KEY)
}

const EARNED_STATUSES_KEY = 'mosquarium_earned_statuses'

export function getEarnedStatuses(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = safeStorage.getItem(EARNED_STATUSES_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function markStatusEarned(status: string): void {
  const current = getEarnedStatuses()
  if (current.includes(status)) return
  safeStorage.setItem(EARNED_STATUSES_KEY, JSON.stringify([...current, status]))
}
